import React, { createContext, PropsWithChildren, useReducer } from "react";
import { ThemeProvider } from "styled-components";
import { GlobalStyleDefaults } from "manifest-components/src/Styles/globals";
import Signup from "manifest-core/classes/employerDashboard/signup";
import RegisterUser from "manifest-core/classes/employerDashboard/registerUser";
import Authentication from "../../utils/authentication";
import AppReducer from "./appReducer";
import { AppState } from "./appState";
import API from "../../utils/api";
import { Actions } from "./appActions";
import { isDevelopmentEnvironment, isLocalEnvironment } from "../../env";

export type AppProviderValue = ReturnType<typeof createProviderValue>;

export const localAPI = {
  getUser: API.user.getUser,
  forgotPassword: API.auth.forgotPassword,
  login: API.auth.login,
  resetPassword: API.auth.resetPassword,
  registerInvitedUser: API.auth.registerInvitedUser,
  changePassword: API.user.updatePassword,
  signup: API.auth.signup,
  updateUser: API.user.updateUser,
  resendVerificationEmail: API.user.resendVerificationEmail,
  verifyUser: API.auth.verifyUser,
  getStatesList: API.user.getStatesList,
};

export type AppProviderAPIValue = typeof localAPI;

const createProviderValue = ({ api = localAPI }) => {
  const [state, dispatch] = useReducer(AppReducer, AppState);

  function setHeaderClass(className: string) {
    dispatch({
      type: Actions.SET_HEADER_CLASS,
      payload: className,
    });
  }

  function updateGlobalCSSVar(property: keyof typeof GlobalStyleDefaults, value: any) {
    dispatch({
      type: Actions.UPDATE_GLOBAL_CSS_VAR,
      payload: {
        property,
        value,
      },
    });
  }

  function revertGlobalCSSVar(property: keyof typeof GlobalStyleDefaults) {
    dispatch({
      type: Actions.UPDATE_GLOBAL_CSS_VAR,
      payload: {
        property,
        value: GlobalStyleDefaults[property],
      },
    });
  }

  const openHamburgerMenu = () => {
    dispatch({
      type: Actions.SET_SHOW_HAMBURGER_MENU,
      payload: state.showHamburgerMenu,
    });
  };

  const login = async ({ password, email, resendEmailVerification = true }) => {
    const response = await api.login({ password, email });
    dispatch({
      type: Actions.CLEAR_DATA,
    });
    Authentication.authenticate({
      accessToken: response.accessToken,
    });

    // If the user is not verified, we should send them another email when they try and login
    const user = await getUser();

    // Store tenantId in sessionStorage for testing purpose.
    Authentication.authenticate({
      accessToken: response.accessToken,
      tenantId: user.tenantId, // QUESTION: Why do we need to put tenantId to sessionStorage?
    });
    if (resendEmailVerification && !user.emailVerified) {
      try {
        await api.resendVerificationEmail(email);
      } catch (e) {
        // Nothing for now, we still let them login
      }
    }
    return user;
  };

  const forgotPassword = async (email: string) => {
    return api.forgotPassword(email);
  };

  const resetPassword = async (params: { confirmationCode: string; password: string; email: string }) => {
    try {
      await api.resetPassword(params);
    } catch (e) {
      throw new Error("Reset Forgot Password Call Failed");
    }
  };

  const getUser = async () => {
    dispatch({
      type: Actions.FETCHING_USER,
    });
    try {
      const user = await api.getUser();
      if (isLocalEnvironment() || isDevelopmentEnvironment()) {
        user.emailVerified = true;
      }
      dispatch({
        type: Actions.GET_USER,
        payload: user,
      });
      return user;
    } catch (e) {
      throw new Error("Failed to fetch user data");
    }
  };

  async function verifyUser(email: string) {
    try {
      return await api.verifyUser(email);
    } catch (e) {
      throw new Error("Email Verification Failed");
    }
  }

  async function registerInvitedUser(params: RegisterUser) {
    return api.registerInvitedUser(params);
  }

  const changePassword = async (params: { previousPassword: string; newPassword: string }) => {
    try {
      await api.changePassword({
        previousPassword: params.previousPassword,
        newPassword: params.newPassword,
      });
      dispatch({
        type: Actions.PASSWORD_CHANGED,
      });
    } catch (e) {
      throw new Error("UNABLE TO CHANGE PASSWORD");
    }
  };

  const createAccount = async (params: Signup) => {
    try {
      await api.signup(params);
    } catch (e) {
      throw new Error("FAILED TO CREATE ACCOUNT");
    }
  };

  const clearData = () => {
    dispatch({
      type: Actions.CLEAR_DATA,
    });
  };

  const setNavigationBlockedReason = (reason: string) => {
    dispatch({
      type: Actions.SET_NAVIGATION_BLOCKED,
      payload: reason,
    });
  };

  const isNavigationBlocked = () => state.navigationBlockedReason !== "";

  const setShowFullScreenLoader = (show: boolean) => {
    dispatch({
      type: Actions.SET_SHOW_FULL_SCREEN_LOADER,
      payload: show,
    });
  };

  const getStatesList = async () => {
    try {
      const response = await api.getStatesList();
      const data = response.staticDataList || [];
      dispatch({
        type: Actions.GET_STATES,
        payload: data,
      });
    } catch (e) {
      throw new Error("Failed to get list of states");
    }
  };

  return {
    ...state,
    clearData,
    getStatesList,
    openHamburgerMenu,
    forgotPassword,
    resetPassword,
    login,
    verifyUser,
    changePassword,
    getUser,
    createAccount,
    revertGlobalCSSVar,
    registerInvitedUser,
    updateGlobalCSSVar,
    isNavigationBlocked,
    setHeaderClass,
    setNavigationBlockedReason,
    setShowFullScreenLoader,
  };
};

export const AppContext = createContext({} as AppProviderValue);

const AppProvider = (props: PropsWithChildren<any>) => {
  const providerValue = createProviderValue(props);
  return (
    <ThemeProvider theme={providerValue.theme}>
      <AppContext.Provider value={providerValue}>{props.children}</AppContext.Provider>
    </ThemeProvider>
  );
};

export default AppProvider;
